import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import { Flex, Pane } from '@leverege/ui-elements';
import { Config } from '@leverege/plugin';
import { ModelFactory, Toolbar } from '@leverege/ui-plugin';
import { GridLoader } from 'react-spinners';
import AggsListModel from "./shared/AggsListModel.js";
import AggItemModel from "./shared/AggItemModel.js";
import { AggViewFactory } from "./types/index.js";
import { buildVariant } from "./util/index.js";
import S from './AggregationView.css';
import { FilterSourcesModel } from "../../filters/index.js";
import { HeaderToolbar } from "../Util.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export default function AggregationsView(props) {
  const Loader = Config.get('LoadingScreen', 'spinner', GridLoader);
  const loaderProps = Config.get('LoadingScreen', 'props', {});
  const refreshTime = Config.get('AggregationsView', 'refreshTime', 60000);
  const client = 'AggsViewer';
  const {
    settingsPath,
    objectType,
    delegate,
    settings,
    relationship,
    vertical,
    filters,
    inCard,
    dispatch,
    data
  } = props;
  const parentLayout = {};
  const childLayout = {};
  let aggFlexVariant = 'row';
  let direction = 'row';
  if (vertical) {
    parentLayout.height = '100%';
    childLayout.width = 200;
    aggFlexVariant = 'col';
    direction = 'column';
  }
  const {
    path
  } = relationship;
  const aggsModel = settings || AggsListModel.create();
  const aggData = useSelector(state => delegate.getList(state, {
    queryName: settingsPath
  })?.aggs);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  useEffect(() => {
    return () => {
      clearTimeout(timer.current);
    };
  }, []);
  const refresh = useCallback(() => {
    const aggs = AggsListModel.toImagineAgg(aggsModel, {
      objectType
    });
    clearTimeout(timer.current);
    if (aggs) {
      const includePageFilters = AggsListModel.getIncludePageFilters(aggsModel);
      const filter = includePageFilters ? FilterSourcesModel.getFilter(filters) : null;
      setLoading(true);
      dispatch(delegate.search({
        aggs,
        filter,
        limit: 0,
        queryName: settingsPath
      })).then(() => {
        setLoading(false);
      });
      timer.current = setTimeout(refresh, refreshTime);
    }
  }, [aggsModel, setLoading, filters, objectType, delegate, dispatch, settingsPath, refreshTime]);
  useEffect(refresh, [aggsModel, filters, refresh, data]);
  const hasHeaders = useMemo(() => {
    return AggsListModel.getAggregations(aggsModel).some(aggModel => {
      const viewModels = aggModel.viewModels || [];
      return viewModels.some(viewModel => {
        const Model = ModelFactory.get(viewModel);
        const headerLabel = Model?.getHeaderLabel?.(viewModel);
        const hasHeader = headerLabel != null && headerLabel.trim().length > 0;
        return typeof Model?.getHeaderLabel === 'function' ? hasHeader : true;
      });
    });
  }, [aggsModel]);
  const aggsMatchContext = {
    use: 'actionBar',
    client,
    objectType,
    path
  };
  const context = useMemo(() => {
    return {
      client,
      clientProps: {
        ...props,
        aggsModel
      }
    };
  }, [props, aggsModel]);
  const variant = AggsListModel.getVariant(aggsModel);
  const toolbar = /*#__PURE__*/_jsx(Toolbar, {
    className: classnames(S.toolbar, !vertical && S.reverse),
    vertical: true,
    variant: "actionBar",
    prefer: "icon",
    matchContext: aggsMatchContext,
    context: context
  });
  return /*#__PURE__*/_jsx(Pane, {
    variant: buildVariant(variant),
    children: /*#__PURE__*/_jsxs(Flex, {
      style: parentLayout,
      direction: direction,
      children: [/*#__PURE__*/_jsx(Flex, {
        className: S.aggregations,
        variant: aggFlexVariant,
        style: childLayout,
        children: AggsListModel.getAggregations(aggsModel).map(aggModel => {
          const views = AggItemModel.getViewModels(aggModel);
          const queryModel = AggItemModel.getModel(aggModel);
          const data = aggData && aggData[aggModel.id];
          return views.map(viewModel => {
            if (!data && loading) {
              return /*#__PURE__*/_jsx(Pane, {
                variant: buildVariant(variant, 'Item'),
                className: classnames(S.aggregation, S.loading),
                children: /*#__PURE__*/_jsx(Loader, {
                  size: loaderProps.size || 20,
                  color: loaderProps.color || 'var( --color-loader, var( --color-brand200, #dedede ) )'
                })
              }, viewModel.id);
            }
            if (data) {
              const viewer = AggViewFactory.create(viewModel, {
                key: viewModel.id,
                className: S.aggregation,
                data,
                vertical,
                aggModel: aggModel.model,
                variant,
                queryModel,
                context,
                hasHeaders
              });
              return viewer;
            }
            return null;
          });
        })
      }), inCard ? /*#__PURE__*/_jsx(HeaderToolbar, {
        inCard: inCard,
        content: toolbar
      }) : toolbar]
    })
  });
}